import React, { useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { getActionData } from '../../../utils';

import type { statusField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/statusField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { useStaleTransitionGuard } from './useStaleTransitionGuard';

const messages = defineMessages({
	action: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.status-field.action',
		defaultMessage: 'Move to {newStatus}',
		description: 'Label for the change action in the status field',
	},
	moveFromOriginalStatus: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.status-field.move-from-original-status',
		defaultMessage: 'Move from {originalStatus} to {newStatus}',
		description: 'Label for the change action in the status field',
	},
});

export type Props = {
	queryRef: statusField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	onInvalidAction?: () => void;
};

export const StatusField = ({
	queryRef,
	action,
	onInvalidAction,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment statusField_updateWorkItemAllFieldTypesActionRenderer on JiraStatusField {
				fieldId
				status {
					name
				}
				transitions @optIn(to: "JiraStatusFieldOptions") {
					edges {
						node {
							transitionId
							to {
								name
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const fieldIdMatches = data?.fieldId === fieldKey;
	const suggestedTransitionId = Number(fieldValue);
	const isValidNumericTransition = !Number.isNaN(suggestedTransitionId);

	const statusName = useMemo(() => {
		if (!fieldIdMatches) {
			return undefined;
		}
		if (!isValidNumericTransition) {
			return typeof fieldValue === 'string' ? String(fieldValue).trim() : undefined;
		}
		return data?.transitions?.edges?.find(
			(edge) => edge?.node?.transitionId === suggestedTransitionId,
		)?.node?.to?.name;
	}, [data, fieldIdMatches, isValidNumericTransition, suggestedTransitionId, fieldValue]);

	useStaleTransitionGuard(!statusName, onInvalidAction);

	if (!fieldIdMatches || !statusName) {
		return null;
	}

	return (
		<Text
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.status-field"
			color="color.text.subtle"
			maxLines={1}
		>
			{data?.status?.name && isValidNumericTransition
				? formatMessage(messages.moveFromOriginalStatus, {
						originalStatus: data?.status?.name,
						newStatus: statusName,
					})
				: formatMessage(messages.action, {
						newStatus: statusName,
					})}
		</Text>
	);
};
